/**
 * The Switchboard login screen (front-door-impl §11.1 — brass-chromed).
 *
 * Mirrors the Agency LoginPage's state machine but in HOUSE BRASS (the root in
 * App.tsx sets `--agent-accent: var(--brass)`, so the shared primitives wear
 * the house metal, never an agent accent — docs/design-system.md). The hub's
 * primary door is the OTP code; a passkey ("Use a passkey") is the one-tap
 * secondary, via @simplewebauthn/browser. On success we go to the directory.
 */
import { useEffect, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { Button, Ember, Field, Tick } from "@charlie/design/primitives";
import { CodeBoxes } from "../components/CodeBoxes";
import {
  startOtp,
  verifyOtp,
  passkeyAuthStart,
  passkeyAuthFinish,
  type VerifyResult,
} from "../lib/api";

type Stage =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "code-sent" }
  | { kind: "rate-limited"; until: number }
  | { kind: "unreachable" };

type CodeError = null | "bad" | "expired" | "locked";

export default function LoginPage() {
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") ?? "");
  const linkState = new URLSearchParams(window.location.search).get("link");
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<CodeError>(linkState === "expired" ? "expired" : null);
  const [shake, setShake] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const emailValid = /\S+@\S+\.\S+/.test(email);

  const send = async () => {
    if (!emailValid) return;
    setStage({ kind: "sending" });
    setCode("");
    setCodeError(null);
    const res = await startOtp(email);
    if (res.ok) {
      setStage({ kind: "code-sent" });
      return;
    }
    if (res.reason === "rate-limited") setStage({ kind: "rate-limited", until: Date.now() + res.retryAfterMs });
    else setStage({ kind: "unreachable" });
  };

  const onVerified = (res: VerifyResult) => {
    if (res.ok) {
      window.location.assign("/");
      return;
    }
    if (res.reason === "unreachable") {
      setStage({ kind: "unreachable" });
      return;
    }
    setCode("");
    setCodeError(res.reason as CodeError);
    if (res.reason === "bad") {
      setShake(true);
      setTimeout(() => setShake(false), 400);
    }
  };

  const submitCode = async (value: string) => {
    if (verifying) return;
    setVerifying(true);
    setCodeError(null);
    const res = await verifyOtp(email, value);
    setVerifying(false);
    onVerified(res);
  };

  const usePasskey = async () => {
    setPasskeyBusy(true);
    setPasskeyError(null);
    try {
      const { ceremonyId, options } = await passkeyAuthStart();
      const response = await startAuthentication({ optionsJSON: options as never });
      const res = await passkeyAuthFinish(ceremonyId, response);
      if (res.ok) {
        window.location.assign("/");
        return;
      }
      setPasskeyError("That passkey didn't check out. Try a code instead.");
    } catch {
      setPasskeyError("Passkey login was cancelled or isn't available here.");
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-full max-w-sm flex-col items-center justify-center gap-6 px-6 py-12 text-center">
      <style>{`
        @keyframes sb-shake { 10%,90%{transform:translateX(-1px)} 30%,70%{transform:translateX(3px)} 50%{transform:translateX(-4px)} }
        [data-shake="1"]{ animation: sb-shake var(--dur-micro,120ms) var(--ease-move) 1; }
        @media (prefers-reduced-motion: reduce){ [data-shake="1"]{ animation: none } }
      `}</style>

      <header className="flex flex-col items-center gap-3">
        <Ember thinking={stage.kind === "sending"}>
          {/* House brass ring + monogram — the hub belongs to no Angel. */}
          <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 border-brass font-display text-[28px] text-brass-deep">
            C
          </div>
        </Ember>
        <h1 className="font-display text-[26px] text-ink">The front door</h1>
        <p className="text-sm text-ink-soft">One sign-in for every Angel you keep.</p>
      </header>

      {stage.kind === "unreachable" ? (
        <div className="flex flex-col items-center gap-3">
          <p className="font-display text-lg text-ink">The front door is offline.</p>
          <p className="max-w-xs text-sm text-ink-soft">
            This isn't you — try again in a moment. Your Angels at home keep working without it.
          </p>
          <Button variant="ghost" onClick={() => setStage({ kind: "idle" })}>
            Try again
          </Button>
        </div>
      ) : stage.kind === "rate-limited" ? (
        <RateLimited until={stage.until} onDone={() => setStage({ kind: "idle" })} />
      ) : stage.kind === "code-sent" ? (
        <div className="flex w-full flex-col items-center gap-4">
          <p className="text-sm text-ink-soft">
            We sent a code to <span className="text-ink">{email}</span> — enter it here.
          </p>
          <CodeBoxes
            value={code}
            onChange={(v) => {
              setCode(v);
              if (codeError) setCodeError(null);
            }}
            onComplete={submitCode}
            shake={shake}
            invalid={codeError === "bad"}
            disabled={verifying}
            autoFocus
          />
          <CodeHelp error={codeError} onResend={send} />
        </div>
      ) : (
        <form
          className="flex w-full flex-col items-stretch gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          {codeError === "expired" && (
            <p className="text-sm text-ink-soft">That link's expired — they're one-time. Send a fresh one below.</p>
          )}
          <Field
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Your email"
            autoFocus
          />
          <Button type="submit" variant="accent" disabled={!emailValid || stage.kind === "sending"}>
            {stage.kind === "sending" ? <span className="italic">sending…</span> : "Send me a code"}
          </Button>
          <Button type="button" variant="quiet" disabled={passkeyBusy} onClick={usePasskey}>
            {passkeyBusy ? <span className="italic">waiting for your passkey…</span> : "Use a passkey"}
          </Button>
          {passkeyError && <p className="text-sm text-ink-soft">{passkeyError}</p>}
        </form>
      )}
    </div>
  );
}

function CodeHelp(props: { error: CodeError; onResend: () => void }) {
  if (props.error === "bad") {
    return <p className="text-sm text-problem">That code didn't match — try again or resend.</p>;
  }
  if (props.error === "expired") {
    return (
      <p className="text-sm text-ink-soft">
        That code's expired — they're one-time.{" "}
        <button className="font-medium text-ink underline-offset-2 hover:underline" onClick={props.onResend}>
          Send a fresh one.
        </button>
      </p>
    );
  }
  if (props.error === "locked") {
    return (
      <p className="text-sm text-problem">
        Too many tries.{" "}
        <button className="font-medium underline-offset-2 hover:underline" onClick={props.onResend}>
          Send a fresh code.
        </button>
      </p>
    );
  }
  return (
    <p className="text-sm text-ink-soft">
      Didn't arrive?{" "}
      <button className="font-medium text-attention underline-offset-2 hover:underline" onClick={props.onResend}>
        Resend
      </button>{" "}
      · check spam.
    </p>
  );
}

function RateLimited(props: { until: number; onDone: () => void }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, props.until - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setRemaining(Math.max(0, props.until - Date.now())), 250);
    return () => clearInterval(t);
  }, [props.until]);
  const totalSecs = Math.ceil(remaining / 1000);
  const mmss = `${Math.floor(totalSecs / 60)}:${String(totalSecs % 60).padStart(2, "0")}`;
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="font-display text-lg text-ink">Let's slow down a second.</p>
      <p className="text-sm text-ink-soft">
        Try again in <span className="font-mono text-ink">{mmss}</span>.
      </p>
      <Button variant="ghost" disabled={remaining > 0} onClick={props.onDone}>
        Try again
      </Button>
    </div>
  );
}
